import {
  Box,
  buildPath,
  buildTemplatePath,
  newToast,
  Page,
  PageRoute,
  SCHOOL_YEAR_ACTIVE_ID,
  Stack,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { ReportOutputFormat } from "components";
import {
  StudentFilter,
  StudentReportEventTrackingRequest,
  StudentReportNscRequest,
  StudentReportServiceDetailRequest,
  StudentReportServiceSummaryRequest,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { EventTrackingRequest } from "./EventTracking/EventTrackingRequest/EventTrackingRequest";
import { EventTrackingResponse } from "./EventTracking/EventTrackingResponse";
import { ServiceDetailRequest } from "./ServiceDetail/ServiceDetailRequest/ServiceDetailRequest";
import { ServiceDetailResponse } from "./ServiceDetail/ServiceDetailResponse";
import { ServiceSummaryRequest } from "./ServiceSummary/ServiceSummaryRequest/ServiceSummaryRequest";
import { ServiceSummaryResponse } from "./ServiceSummary/ServiceSummaryResponse";
import { StudentReportPageHeader } from "./StudentReportPageHeader";
import { StudentReportType } from "./StudentReportType";
import { NscRequest } from "./Nsc/NscRequest/NscRequest";

const routeName = "studentReport";

export const studentReportPageRoute: PageRoute = {
  handle: "Student",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ReportType = "Event Tracking" | "Services" | "Service Summary" | "NSC";
export type ScreenType = "config" | "report";

export type ReportSelection = {
  name: ReportType;
  generateTypes: ReportOutputFormat[];
};

const reportTypes: ReportSelection[] = [
  { name: "Event Tracking", generateTypes: ["on-screen", "pdf", "excel"] },
  { name: "Services", generateTypes: ["on-screen", "pdf", "excel"] },
  { name: "Service Summary", generateTypes: ["on-screen", "pdf", "excel"] },
  { name: "NSC", generateTypes: ["excel"] },
];

export const StudentReportPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedReportType, setSelectedReportType] = useState<ReportType | undefined>();
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [visitedReportTypes, setVisitedReportTypes] = useState<string[]>([]);
  const [screen, setScreen] = useState<ScreenType>("config");

  // this filter helps maintain filters for certain while toggling between reports
  //const [globalFilter, setGlobalFilter] = useState<StudentReportGlobalFilter>({});

  // report filters & results
  const [commonFilter, setCommonFilter] = useState<StudentFilter>({
    schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
  } as StudentFilter);

  const [eventTrackingRequest, setEventTrackingRequest] = useState<StudentReportEventTrackingRequest>({});
  const [serviceDetailRequest, setServiceDetailRequest] = useState<StudentReportServiceDetailRequest>({});
  const [serviceSummaryRequest, setServiceSummaryRequest] = useState<StudentReportServiceSummaryRequest>({});
  const [nscRequest, setNscRequest] = useState<StudentReportNscRequest>({});

  const [reportResponse, setReportResponse] = useState<any>();

  useEffect(() => {
    if (selectedReportType && !visitedReportTypes.includes(selectedReportType))
      setVisitedReportTypes((visitedReportTypes) => [...visitedReportTypes, selectedReportType]);
  }, [selectedReportType]);

  const generateReport = () => {
    setValidationErrors({});

    switch (selectedReportType) {
      case "Event Tracking":
        ApiService.studentReports
          .generateEventTracking({ ...eventTrackingRequest, studentFilter: commonFilter })
          .then((response) => {
            setScreen("report");
            setReportResponse(response);
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
        break;
      case "Services":
        ApiService.studentReports
          .generateServiceDetail({ ...serviceDetailRequest, studentFilter: commonFilter })
          .then((response) => {
            setScreen("report");
            setReportResponse(response);
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
        break;
      case "Service Summary":
        ApiService.studentReports
          .generateServiceSummary({ ...serviceSummaryRequest, studentFilter: commonFilter })
          .then((response) => {
            setScreen("report");
            setReportResponse(response);
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });
        break;
      case "NSC":
        ApiService.studentReports
          .validateNsc(nscRequest)
          .then(() => {
            ApiService.studentReports.generateNsc(nscRequest).then(async (response) => {
              const blob = response!.data;
              const url = window.URL.createObjectURL(new Blob([blob]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", "NSC_Request.xlsx");
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            });
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            setValidationErrors(ex.response);
          });

        break;
    }
  };

  return (
    <Page
      isForm
      header={
        <StudentReportPageHeader
          handle={studentReportPageRoute.handle}
          reportTypes={reportTypes}
          selectedReportType={selectedReportType}
          screen={screen}
          setScreen={setScreen}
          generateReport={generateReport}
        />
      }
    >
      <Box show={screen === "config"}>
        <Stack spacing="lg">
          <StudentReportType
            reportTypes={reportTypes}
            selectedReportType={selectedReportType}
            setSelectedReportType={setSelectedReportType}
          />

          <Box show={selectedReportType === "Event Tracking"}>
            <EventTrackingRequest
              initialized={visitedReportTypes.includes("Event Tracking")}
              request={eventTrackingRequest}
              setRequest={setEventTrackingRequest}
              globalCommonFilter={commonFilter}
              setGlobalCommonFilter={setCommonFilter}
              validationErrors={validationErrors}
            />
          </Box>

          <Box show={selectedReportType === "Services"}>
            <ServiceDetailRequest
              initialized={visitedReportTypes.includes("Services")}
              request={serviceDetailRequest}
              setRequest={setServiceDetailRequest}
              globalCommonFilter={commonFilter}
              setGlobalCommonFilter={setCommonFilter}
              validationErrors={validationErrors}
            />
          </Box>

          <Box show={selectedReportType === "Service Summary"}>
            <ServiceSummaryRequest
              initialized={visitedReportTypes.includes("Service Summary")}
              request={serviceSummaryRequest}
              setRequest={setServiceSummaryRequest}
              globalCommonFilter={commonFilter}
              setGlobalCommonFilter={setCommonFilter}
              validationErrors={validationErrors}
            />
          </Box>

          <Box show={selectedReportType === "NSC"}>
            <NscRequest
              initialized={visitedReportTypes.includes("NSC")}
              request={nscRequest}
              setRequest={setNscRequest}
              validationErrors={validationErrors}
            />
          </Box>
        </Stack>
      </Box>

      {screen === "report" && (
        <Box>
          {selectedReportType === "Event Tracking" && <EventTrackingResponse response={reportResponse} />}
          {selectedReportType === "Services" && <ServiceDetailResponse response={reportResponse} />}
          {selectedReportType === "Service Summary" && <ServiceSummaryResponse response={reportResponse} />}
        </Box>
      )}
    </Page>
  );
};
